/**
 * The dashboard's primary navigation model.
 *
 * Each item carries the RBAC `resource`/`action` it gates on so the sidebar can
 * hide entries the session can't access (via `usePermissions().can`). Icons come
 * from lucide-react (ui-core's icon set), labels are plain strings — the source
 * of truth for the left nav, breadcrumbs and the command menu.
 */
import {
  Braces,
  Compass,
  LayoutDashboard,
  ArrowLeftRight,
  Wallet,
  WalletCards,
  Network,
  Share2,
  Microscope,
  FileCode2,
  Sparkles,
  BarChart3,
  LineChart,
  Table2,
  Activity,
  Workflow,
  Play,
  Waypoints,
  BellRing,
  Eye,
  Radar,
  Users,
  Settings,
  type LucideIcon,
} from "lucide-react";
import { RESOURCES, ROUTES } from "./constants";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** RBAC resource gating visibility (defaults to always-visible when omitted). */
  resource?: string;
  /** RBAC action (defaults to `list`). */
  action?: string;
  /** Optional grouping header. */
  section?: string;
}

export const NAV_ITEMS: NavItem[] = [
  // Onboarding guide — ungated (every signed-in user can follow setup).
  { to: ROUTES.gettingStarted, label: "Getting Started", icon: Compass, section: "Explore" },
  { to: ROUTES.overview, label: "Overview", icon: LayoutDashboard, resource: RESOURCES.overview, section: "Explore" },
  { to: ROUTES.transactions, label: "Transactions", icon: ArrowLeftRight, resource: RESOURCES.transactions, section: "Explore" },
  { to: ROUTES.addresses, label: "Addresses", icon: Wallet, resource: RESOURCES.addresses, section: "Explore" },
  // One entry for what used to be "Entities" and "Groups": the same catalog,
  // split by kind rather than by page. Still gated on `entities` — the two
  // resources have never been granted apart, and gating on the narrower of them
  // would hide groups from someone entitled to see them.
  { to: ROUTES.catalog, label: "Catalog", icon: Network, resource: RESOURCES.entities, section: "Explore" },
  { to: ROUTES.graphs, label: "Graphs", icon: Share2, resource: RESOURCES.graphs, section: "Explore" },
  // Investigations are a client-side collaborative workspace (no backend RBAC
  // resource yet), so — like Wallet — the entry is intentionally ungated.
  { to: ROUTES.investigations, label: "Investigations", icon: Microscope, section: "Explore" },
  { to: ROUTES.contracts, label: "Contracts", icon: FileCode2, resource: RESOURCES.contracts, section: "Explore" },

  { to: ROUTES.askAi, label: "Ask AI", icon: Sparkles, resource: RESOURCES.askAi, section: "Analyze" },
  { to: ROUTES.stats, label: "Stats", icon: BarChart3, resource: RESOURCES.stats, section: "Analyze" },
  // Analytics reads over the metadata-registry (decoded-fact EAV + views). Gated
  // on the read verb the Reader tier carries (`metadata-registry:analytics`).
  { to: ROUTES.analytics, label: "Analytics", icon: LineChart, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Analyze" },
  // Datadog-style transaction explorer over the same decoded-metadata reads;
  // shares the `metadata-registry:analytics` read verb.
  { to: ROUTES.analyticsExplorer, label: "Explorer", icon: Table2, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Analyze" },
  // How those transactions are decoded — the definition catalogue + the tenant's
  // own `definition_patch` overrides. Same resource, read with `analytics`;
  // authoring is gated per-control by `edit` / `create`.
  { to: ROUTES.analyticsProtocols, label: "Protocol decoding", icon: Braces, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Analyze" },
  // Wallets ranked by conduct and realised ETH. In "Analyze" rather than beside
  // Situations in "Automate", and gated on the analytics read rather than on
  // `watches`, because that is what it IS: the same decoded facts, aggregated by
  // address through the metadata-registry views. Situations reads detections from
  // pipeline-mgmt-api; this reads the warehouse.
  { to: ROUTES.actors, label: "Actors", icon: Radar, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Analyze" },
  { to: ROUTES.activity, label: "Activity", icon: Activity, resource: RESOURCES.activity, section: "Analyze" },

  { to: ROUTES.pipelines, label: "Pipelines", icon: Workflow, resource: RESOURCES.pipelines, section: "Automate" },
  { to: ROUTES.runs, label: "Runs", icon: Play, resource: RESOURCES.runs, action: "process", section: "Automate" },
  { to: ROUTES.tracers, label: "Tracers", icon: Waypoints, resource: RESOURCES.tracers, section: "Automate" },
  { to: ROUTES.alerts, label: "Alerts", icon: BellRing, resource: RESOURCES.alerts, section: "Automate" },
  { to: ROUTES.watches, label: "Watches", icon: Eye, resource: RESOURCES.watches, section: "Automate" },
  // What the watches are watching FOR, already detected and grouped. Gated on
  // `watches` rather than `pipelines`: the rows come from pipeline-mgmt-api, but
  // reading the platform's own detections is the reason a customer holds a watch
  // permission at all, and gating it on pipelines would hide it from every
  // tenant who does not author pipelines — which is most of them.
  { to: ROUTES.situations, label: "Situations", icon: Radar, resource: RESOURCES.watches, section: "Automate" },
  // The same detections re-keyed on WHERE THE MONEY WENT. Beside Situations and
  // on the same `watches` resource for the same reason: both read the platform's
  // own detections out of pipeline-mgmt-api, and gating either on `pipelines`
  // would hide it from every tenant who does not author pipelines.
  { to: ROUTES.flows, label: "Flows", icon: Waypoints, resource: RESOURCES.watches, section: "Automate" },

  // Wallet is a purely client-side feature (browser wallet via wagmi) with no
  // backend `wallets` RBAC resource, so it is intentionally NOT gated — every
  // signed-in user can connect/inspect their wallet.
  { to: ROUTES.wallet, label: "Wallet", icon: WalletCards, section: "Settings" },
  { to: ROUTES.team, label: "Team", icon: Users, resource: RESOURCES.team, section: "Settings" },
  { to: ROUTES.settings, label: "Settings", icon: Settings, resource: RESOURCES.settings, section: "Settings" },
];

/** Distinct section headers, in first-seen order. */
export const NAV_SECTIONS: string[] = NAV_ITEMS.reduce<string[]>((acc, item) => {
  if (item.section && !acc.includes(item.section)) acc.push(item.section);
  return acc;
}, []);
